import { cva, type VariantProps } from 'class-variance-authority';

/**
 * HEN Icon variants using HEN Design System tokens.
 * All styling references design tokens defined in tailwind.config.ts.
 */
export const iconVariants = cva(
  [
    'inline-block',
    'shrink-0',
    'aspect-square',
    'stroke-current',
    'fill-none',
    'vertical-align-middle',
  ],
  {
    variants: {
      size: {
        // TODO: Check with creative on these sizes
        full: 'w-full h-full',
        sm: 'w-4 h-4',
        md: 'w-6 h-6',
        lg: 'w-8 h-8',
        xl: 'w-10 h-10',
        '2xl': 'w-12 h-12',
        '3xl': 'w-14 h-14',
      },
      color: {
        // TODO: Check with creative on these colors
        default: 'text-current',
        white: 'text-white',
        primary: 'text-primary-700',
        secondary: 'text-secondary-700',
        black: 'text-black',
        neutral: 'text-neutral-700',
      },
      // TODO: Check with creative on these background colors
      background: {
        default: 'bg-transparent',
        neutral: 'bg-neutral-100 rounded-full',
        primary: 'bg-primary-100 rounded-full',
        secondary: 'bg-secondary-100 rounded-full',
        black: 'bg-black rounded-full',
      },
    },
    compoundVariants: [
      // Adjust icon size + add padding to maintain visual size
      // sm: 16px icon → need 24px container (16px + 8px padding)
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: 'sm',
        class: 'w-6 h-6 p-1', // 24px total (icon appears ~16px)
      },
      // md: 24px icon → need 32px container (24px + 8px padding)
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: 'md',
        class: 'w-8 h-8 p-1', // 32px total (icon appears ~24px)
      },
      // lg: 32px icon → need 40px container (32px + 8px padding)
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: 'lg',
        class: 'w-10 h-10 p-1', // 40px total (icon appears ~32px)
      },
      // xl: 40px icon → need 48px container (40px + 8px padding)
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: 'xl',
        class: 'w-12 h-12 p-1', // 48px total (icon appears ~40px)
      },
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: '2xl',
        class: 'w-14 h-14 p-1', // 56px total (icon appears ~48px)
      },
      {
        background: ['neutral', 'primary', 'secondary', 'black'],
        size: '3xl',
        class: 'w-16 h-16 p-1', // 64px total (icon appears ~56px)
      },
    ],
    defaultVariants: {
      size: 'md',
      color: 'default',
      background: 'default',
    },
  },
);

export type IconVariantProps = VariantProps<typeof iconVariants>;
